/**
 * AuditLogPanel — admin viewer for /api/admin/audit-log.
 *
 * Visible only to founder + sales_manager (`show_audit_log` ui_flag).
 * Filters: action prefix, policy tag, actor email/user_id, target id.
 * Pagination by `skip` + `limit`.
 *
 * Hashes are shown truncated (first 12 chars) but copyable on click.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Search, Shield, Copy, Tag, User, Clock, Hash, Download, ClipboardCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import SuspiciousActivityCard from "./SuspiciousActivityCard";

const POLICY_TAGS = [
  { value: "", label: "All policy tags" },
  { value: "C1", label: "C1 — Master rate card" },
  { value: "C2", label: "C2 — Deal rate card" },
  { value: "C3", label: "C3 — Users" },
  { value: "C4", label: "C4 — Trials" },
  { value: "C5", label: "C5 — Campaigns" },
];

// One-click prefixes for the governance actions that matter most when a
// methodology output is challenged (SME sign-off + advice→delivery promotion).
const QUICK_FILTERS = [
  { value: "", label: "All actions" },
  { value: "delivery_tom.item.", label: "TOM sign-off & promotion" },
  { value: "erp_overlay.mapping.status.", label: "ERP/Salesforce SME sign-off" },
  { value: "delivery_tom.item.promote_to_raid", label: "TOM → RAID promotions" },
  { value: "session.", label: "Sign-in / sign-out" },
];

const formatAt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const copyToClipboard = async (value, label) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed");
  }
};

const HashBadge = ({ value, label }) => {
  if (!value) {
    return <span className="ek-text-muted text-[11px]">—</span>;
  }
  const truncated = value.slice(0, 12);
  return (
    <button
      type="button"
      onClick={() => copyToClipboard(value, label)}
      className="font-mono text-[11px] ek-text-soft hover:text-[#7737F6] inline-flex items-center gap-1 group"
      data-testid={`hash-${label.toLowerCase()}`}
      title={`${label}: ${value} (click to copy)`}
    >
      {truncated}…
      <Copy size={10} className="opacity-0 group-hover:opacity-100" />
    </button>
  );
};

const ResultPill = ({ result }) => {
  const colour = result === "success" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : result === "denied" ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-red-50 text-red-700 border-red-200";
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-medium tracking-[0.08em] uppercase border ${colour}`}>
      {result || "—"}
    </span>
  );
};

const prettyCheck = (id) => {
  if (!id) return "—";
  if (id.startsWith("sector_calibration_")) {
    return "Sector calibration — " + id.replace("sector_calibration_", "").replace(/_/g, " ");
  }
  return id.replace(/_/g, " ");
};

const QaAckDigestCard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/enterprise360/qa/acknowledgements");
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load QA acknowledgements");
      }
    })();
  }, []);

  if (error) return null;
  if (!data) return null;

  return (
    <div className="border ek-line ek-surface" data-testid="qa-ack-digest">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-zinc-50"
        data-testid="qa-ack-digest-toggle"
      >
        <div className="flex items-center gap-2">
          <ClipboardCheck size={16} className="text-[#7737F6]" />
          <span className="text-[13px] font-semibold ek-text">Enterprise360 QA acknowledgements</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#7737F6]/10 text-[#5e2bcc] font-medium" data-testid="qa-ack-digest-count">
            {data.total} acknowledgement{data.total === 1 ? "" : "s"}
          </span>
        </div>
        <ChevronDown size={16} className={`ek-text-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-[12px] ek-text-muted mb-3">
            Who reviewed and accepted a QA warning at export, across all live engagements — governance evidence when a pack is challenged.
          </p>
          {data.total === 0 ? (
            <div className="text-[12px] ek-text-muted py-2" data-testid="qa-ack-digest-empty">
              No QA warnings have been acknowledged yet.
            </div>
          ) : (
            <>
              {data.by_consultant?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3" data-testid="qa-ack-digest-rollup">
                  {data.by_consultant.map((c) => (
                    <span key={c.by_email} className="text-[11px] px-2.5 py-1 border ek-line rounded-full ek-text-soft">
                      <User size={11} className="inline mr-1 ek-text-muted" />
                      {c.by_name || c.by_email} · <strong>{c.count}</strong>
                    </span>
                  ))}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="min-w-full text-[12px]" data-testid="qa-ack-digest-table">
                  <thead className="ek-surface-2">
                    <tr className="text-left text-[10px] tracking-[0.1em] uppercase ek-text-muted">
                      <th className="px-3 py-2 font-medium">When</th>
                      <th className="px-3 py-2 font-medium">Client</th>
                      <th className="px-3 py-2 font-medium">QA warning</th>
                      <th className="px-3 py-2 font-medium">Accepted by</th>
                      <th className="px-3 py-2 font-medium">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((r, i) => (
                      <tr key={i} className="border-b ek-line" data-testid={`qa-ack-row-${i}`}>
                        <td className="px-3 py-2 ek-text-soft whitespace-nowrap">{formatAt(r.at)}</td>
                        <td className="px-3 py-2 ek-text">{r.client_name}</td>
                        <td className="px-3 py-2 ek-text-soft">{prettyCheck(r.check_id)}</td>
                        <td className="px-3 py-2 ek-text-soft">{r.by_name || r.by_email}</td>
                        <td className="px-3 py-2 ek-text-muted italic">{r.note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const AuditLogPanel = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionPrefix, setActionPrefix] = useState("");
  const [policyTag, setPolicyTag] = useState("");
  const [actorUserId, setActorUserId] = useState("");
  const [skip, setSkip] = useState(0);
  const PAGE_SIZE = 50;

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (actionPrefix) params.action_prefix = actionPrefix;
      if (policyTag) params.policy_tag = policyTag;
      if (actorUserId) params.actor_user_id = actorUserId;
      const { data } = await api.get("/admin/audit-log", { params });
      setEntries(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load audit log");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [actionPrefix, policyTag, actorUserId, skip]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (actionPrefix) params.action_prefix = actionPrefix;
      if (policyTag) params.policy_tag = policyTag;
      if (actorUserId) params.actor_user_id = actorUserId;
      const res = await api.get("/admin/audit-log/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ekselens_audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit log exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-audit-log-panel">
      <div>
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-[#7737F6]" />
          <div className="ek-eyebrow text-[#7737F6] text-[11px]">Governance</div>
        </div>
        <h2 className="ek-heading text-2xl ek-text mt-2">Audit log</h2>
        <p className="text-[14px] ek-text-soft font-light mt-2 max-w-3xl">
          Append-only record of every state-changing admin action — including sign-in / sign-out
          (incl. idle &amp; session-cap timeouts) and board-level assurance sign-offs. Each entry is
          anchored by SHA-256 hashes of the before/after state. Retention is 7 years. IPs are stored
          with the last octet zeroed (GDPR Recital 26). Use <strong>Export CSV</strong> for an
          auditor-ready evidence file.
        </p>
      </div>

      <SuspiciousActivityCard
        onClickActor={(actor) => {
          // Filter the table to entries from this actor by setting the
          // actor_user_id filter to their email/user_id and resetting page.
          setSkip(0);
          setActorUserId(actor);
        }}
      />

      <QaAckDigestCard />

      {/* Quick filters — governance/methodology sign-off shortcuts */}
      <div className="flex flex-wrap items-center gap-2" data-testid="audit-log-quick-filters">
        <span className="text-[11px] tracking-[0.12em] uppercase ek-text-muted mr-1">Quick filters</span>
        {QUICK_FILTERS.map((q) => (
          <button
            key={q.value || "all"}
            type="button"
            onClick={() => { setSkip(0); setActionPrefix(q.value); }}
            className={`px-3 py-1.5 text-[11.5px] rounded-full border transition-colors ${
              actionPrefix === q.value
                ? "bg-[#7737F6] text-white border-[#7737F6]"
                : "ek-line ek-text-soft hover:bg-zinc-50"
            }`}
            data-testid={`audit-quick-${q.value ? q.value.replace(/[^a-z]/gi, "-") : "all"}`}
          >
            {q.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 p-4 ek-surface-2 border ek-line">
        <div>
          <label className="block text-[11px] tracking-[0.12em] uppercase ek-text-muted mb-1">Action prefix</label>
          <input
            type="text"
            value={actionPrefix}
            onChange={(e) => { setSkip(0); setActionPrefix(e.target.value); }}
            placeholder="e.g. rate_card."
            className="px-3 py-2 border ek-line text-[13px] focus:border-[#7737F6] focus:outline-none w-56"
            data-testid="audit-log-filter-action"
          />
        </div>
        <div>
          <label className="block text-[11px] tracking-[0.12em] uppercase ek-text-muted mb-1">Policy tag</label>
          <select
            value={policyTag}
            onChange={(e) => { setSkip(0); setPolicyTag(e.target.value); }}
            className="px-3 py-2 border ek-line text-[13px] focus:border-[#7737F6] focus:outline-none w-56 ek-surface"
            data-testid="audit-log-filter-policy-tag"
          >
            {POLICY_TAGS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] tracking-[0.12em] uppercase ek-text-muted mb-1">Actor user_id</label>
          <input
            type="text"
            value={actorUserId}
            onChange={(e) => { setSkip(0); setActorUserId(e.target.value); }}
            placeholder="user_..."
            className="px-3 py-2 border ek-line text-[13px] focus:border-[#7737F6] focus:outline-none w-56"
            data-testid="audit-log-filter-actor"
          />
        </div>
        <button
          type="button"
          onClick={fetchEntries}
          className="px-4 py-2 bg-[#7737F6] text-white text-[12px] font-medium tracking-[0.08em] uppercase hover:bg-[#5e2bcc] transition-colors inline-flex items-center gap-1.5"
          data-testid="audit-log-refresh"
        >
          <Search size={14} />
          Refresh
        </button>
        <button
          type="button"
          onClick={exportCsv}
          disabled={exporting}
          className="px-4 py-2 border ek-line ek-text-soft text-[12px] font-medium tracking-[0.08em] uppercase hover:bg-zinc-100 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="audit-log-export-csv"
        >
          <Download size={14} />
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {/* Table */}
      {error ? (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-[13px]" data-testid="audit-log-error">
          {error}
        </div>
      ) : loading && entries.length === 0 ? (
        <div className="p-6 ek-text-muted text-sm">Loading audit log…</div>
      ) : entries.length === 0 ? (
        <div className="p-6 ek-text-muted text-sm" data-testid="audit-log-empty">
          No audit entries match the current filters.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-[12px]" data-testid="audit-log-table">
            <thead className="ek-surface-2">
              <tr className="text-left text-[10px] tracking-[0.1em] uppercase ek-text-muted">
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">Action</th>
                <th className="px-3 py-2 font-medium">Tag</th>
                <th className="px-3 py-2 font-medium">Actor</th>
                <th className="px-3 py-2 font-medium">Target</th>
                <th className="px-3 py-2 font-medium">Before</th>
                <th className="px-3 py-2 font-medium">After</th>
                <th className="px-3 py-2 font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b ek-line hover:bg-zinc-50/60" data-testid={`audit-row-${e.id}`}>
                  <td className="px-3 py-2 ek-text-soft whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <Clock size={11} className="ek-text-muted" />
                      {formatAt(e.at)}
                    </div>
                  </td>
                  <td className="px-3 py-2 ek-text font-mono text-[11px]">
                    {e.action}
                    {e.extra?.reason && (
                      <div className="text-[10px] ek-text-muted mt-0.5">reason: {e.extra.reason}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-block px-2 py-0.5 text-[10px] font-mono bg-[#7737F6]/10 text-[#5e2bcc] border border-[#7737F6]/20">
                      {e.policy_tag || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 ek-text-soft">
                    <div className="flex items-center gap-1">
                      <User size={11} className="ek-text-muted" />
                      <span className="font-mono text-[11px]">{e.actor_email || e.actor_user_id || "—"}</span>
                    </div>
                    <div className="text-[10px] ek-text-muted mt-0.5">role: {e.actor_role}</div>
                    {e.impersonated_as_user_id && (
                      <div className="text-[10px] text-amber-700 mt-0.5">
                        impersonating: <span className="font-mono">{e.impersonated_as_user_id}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 ek-text-soft">
                    <div className="font-mono text-[11px]">{e.target?.collection || "—"}</div>
                    {e.target?.id && (
                      <div className="text-[10px] ek-text-muted font-mono mt-0.5">{e.target.id}</div>
                    )}
                    {e.target?.fields_touched?.length > 0 && (
                      <div className="text-[10px] ek-text-muted mt-0.5">
                        fields: {e.target.fields_touched.join(", ")}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <HashBadge value={e.before_hash} label="before_hash" />
                  </td>
                  <td className="px-3 py-2">
                    <HashBadge value={e.after_hash} label="after_hash" />
                  </td>
                  <td className="px-3 py-2">
                    <ResultPill result={e.result} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between text-[12px] ek-text-muted">
        <div>
          Showing entries {skip + 1}–{skip + entries.length}{" "}
          {entries.length === PAGE_SIZE ? "(more available)" : ""}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            className="px-3 py-1.5 border ek-line disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-50"
            data-testid="audit-log-page-prev"
          >
            ← Previous
          </button>
          <button
            type="button"
            disabled={entries.length < PAGE_SIZE}
            onClick={() => setSkip(skip + PAGE_SIZE)}
            className="px-3 py-1.5 border ek-line disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-50"
            data-testid="audit-log-page-next"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogPanel;
